import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  RotateCcw,
  Ban,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { AppRoutes } from '@/constants/routes';

// ── Types ──────────────────────────────────────────────────────────────────

type JobStatus =
  | 'Pending'
  | 'Scheduled'
  | 'Running'
  | 'Completed'
  | 'Failed'
  | 'Retrying'
  | 'Cancelled'
  | 'DeadLetter';

interface BackgroundJobSummary {
  id: string;
  jobType: string;
  jobName: string;
  status: JobStatus;
  scheduledTime: string;
  startedTime?: string;
  completedTime?: string;
  retryCount: number;
  maxRetries: number;
  errorMessage?: string;
  triggeredBy: string;
  createdDate: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  JobStatus,
  { label: string; color: string; icon: React.ElementType }
> = {
  Pending:    { label: 'Pending',    color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800', icon: Clock },
  Scheduled:  { label: 'Scheduled',  color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800',    icon: Clock },
  Running:    { label: 'Running',    color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800', icon: Activity },
  Completed:  { label: 'Completed',  color: 'bg-green-100 dark:bg-green-900/30 text-green-800',  icon: CheckCircle },
  Failed:     { label: 'Failed',     color: 'bg-red-100 dark:bg-red-900/30 text-red-800',      icon: XCircle },
  Retrying:   { label: 'Retrying',   color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800',icon: RotateCcw },
  Cancelled:  { label: 'Cancelled',  color: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',    icon: Ban },
  DeadLetter: { label: 'Dead Letter',color: 'bg-red-200 text-red-900',      icon: AlertTriangle },
};

const StatusChip = ({ status }: { status: JobStatus }) => {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.Pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
};

const fmtDateTime = (iso?: string) =>
  iso ? new Date(iso).toLocaleString() : '—';

// ── Page ──────────────────────────────────────────────────────────────────

export default function SchedulerJobsPage() {
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<JobStatus | ''>('');
  const [typeFilter,   setTypeFilter]   = useState('');
  const [isLoading]                     = useState(false);

  // In a real implementation these would come from an API call via React Query.
  // Placeholder for the scheduler jobs table.
  const jobs: BackgroundJobSummary[] = [];

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Background Jobs</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Monitor and manage automated background job executions.
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* ── Summary Cards ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {(
          [
            { label: 'Pending',   value: 0, color: 'text-yellow-600 dark:text-yellow-400', status: 'Pending'   },
            { label: 'Running',   value: 0, color: 'text-indigo-600 dark:text-indigo-400', status: 'Running'   },
            { label: 'Failed',    value: 0, color: 'text-red-600 dark:text-red-400',    status: 'Failed'    },
            { label: 'Dead Letter', value: 0, color: 'text-red-900', status: 'DeadLetter'},
          ] as const
        ).map((card) => (
          <button
            key={card.label}
            onClick={() => setStatusFilter(card.status)}
            className={`bg-white dark:bg-slate-800 border rounded-xl p-4 text-left hover:shadow-md transition-shadow ${
              statusFilter === card.status ? 'ring-2 ring-indigo-500' : ''
            }`}
          >
            <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
            <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
          </button>
        ))}
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as JobStatus | '')}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Statuses</option>
            {(Object.keys(STATUS_CONFIG) as JobStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_CONFIG[s].label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Job Type</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Types</option>
            <option value="RecurringJournalGeneration">Recurring Journal</option>
            <option value="AccrualRecognition">Accrual Recognition</option>
            <option value="GlAllocationExecution">GL Allocation</option>
            <option value="AutoReversalGeneration">Auto Reversal</option>
          </select>
        </div>
        {(statusFilter || typeFilter) && (
          <button
            onClick={() => { setStatusFilter(''); setTypeFilter(''); }}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 underline self-end mb-2"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              {['Job Name', 'Type', 'Status', 'Scheduled', 'Started', 'Duration', 'Retries', 'Triggered By'].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                  <Activity size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No background jobs found.</p>
                  <p className="text-xs mt-1">
                    Jobs are created automatically by the scheduler when automation is configured.
                  </p>
                </td>
              </tr>
            ) : (
              jobs.map((job) => {
                const durationMs =
                  job.startedTime && job.completedTime
                    ? new Date(job.completedTime).getTime() -
                      new Date(job.startedTime).getTime()
                    : undefined;

                return (
                  <tr
                    key={job.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                    onClick={() =>
                      navigate(AppRoutes.SCHEDULER_JOB_DETAIL.replace(':id', job.id))
                    }
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{job.jobName}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{job.jobType}</td>
                    <td className="px-4 py-3">
                      <StatusChip status={job.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{fmtDateTime(job.scheduledTime)}</td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{fmtDateTime(job.startedTime)}</td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {durationMs != null ? `${(durationMs / 1000).toFixed(1)}s` : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {job.retryCount}/{job.maxRetries}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{job.triggeredBy}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
